// external
import React, { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { FieldControl, Label, Select, SelectOption } from '@americanexpress/dls-react';

// components
import DefaultFormButtons from '../../defaultFormButtons/DefaultFormButtons';
import ManualAccountService from 'src/services/manualAccount.service';

// styles
import styles from '../../addAccountFormContainer.module.scss';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import FormatService from 'src/services/format.service';
import colors from 'src/styles/colors.module.scss';
import { AddAccountFormViewModel } from 'src/types/view-models/addAccount/addAccount.view.model';
import LiteralsService from 'src/services/literals/LiteralsService';
import { InvestmentHoldingType } from 'src/types/investment-holding.model';
import { CryptoModel } from 'src/types/crypto.model';
import CryptoService from 'src/services/CryptoService';
import CurrencyInput from 'src/components/formik-fields/currency-input/CurrencyInput';

/** This component does not use AddAccountForm because it requires access to the form values for rendering */
export default function CryptocurrencyForm({
    manualAccount,
    onSubmit,
    handleBack
}: AddAccountFormViewModel): JSX.Element {
    const [crypto, setCrypto] = useState<CryptoModel[]>();

    useEffect(() => {
        void (async () => {
            const crypto = await CryptoService.get();
            setCrypto(crypto);
        })();
    }, []);

    const secondaryOnclick = (): void => {
        void ManualAccountService.deleteAccount(manualAccount);
        handleBack();
    };

    if (!crypto) return <></>;

    return (
        <Formik
            initialValues={{
                ...{
                    name: '',
                    amount: ''
                },
                ...manualAccount?.investmentHolding
            }}
            validationSchema={Yup.object({
                amount: Yup.number()
                    .required(LiteralsService.getLiteral('Onboarding.forms.inputValidationNull'))
                    .typeError(LiteralsService.getLiteral('Onboarding.forms.inputValidationTypeNumber'))
            })}
            onSubmit={(values) => {
                void onSubmit(values, manualAccount, false, InvestmentHoldingType.Crypto);
            }}
        >
            {({ values }) => {
                return (
                    <Form className={styles.form}>
                        <FieldControl className="account-selector-container">
                            <Label htmlFor="name" id="name">
                                {LiteralsService.getLiteral('Onboarding.forms.inputLabels.currencyName')}
                            </Label>
                            <Select id="name">
                                <SelectOption />
                                {crypto.map((c, i) => (
                                    <SelectOption key={`crypto-row-${i}`} value={c.name}>
                                        {c.name}
                                    </SelectOption>
                                ))}
                            </Select>
                        </FieldControl>
                        <CurrencyInput
                            id="amount"
                            label={LiteralsService.getLiteral('Onboarding.forms.inputLabels.coinQuantity')}
                        />
                        {values.price && values.quantity && (
                            <div>
                                <Typography variant={TypographyVariants.BODY_BOLD}>
                                    {LiteralsService.getLiteral('Onboarding.forms.inputLabels.total')}
                                </Typography>
                                <Typography variant={TypographyVariants.HEADING_XL} className={colors.textStrong}>
                                    {FormatService.format(values.price * values.quantity)}
                                </Typography>
                            </div>
                        )}
                        <DefaultFormButtons update={!!manualAccount} secondaryOnclick={secondaryOnclick} />
                    </Form>
                );
            }}
        </Formik>
    );
}
